import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, type ActivityEntry } from '@/lib/api';
import { Card, CardBody } from '@/components/ui/card';
import { Badge, statusTone } from '@/components/ui/badge';
import { relativeTime } from '@/lib/utils';

export function ActivityPage() {
  const activity = useQuery({
    queryKey: ['activity', { limit: 200 }],
    queryFn: () => api.get<{ activity: ActivityEntry[] }>('/api/activity?limit=200'),
    refetchInterval: 10_000,
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Activity</h1>
        <p className="text-sm text-slate-500 mt-1">Every state-changing operation, with the operator who issued it.</p>
      </div>

      <Card>
        <CardBody className="p-0">
          {activity.isLoading ? (
            <div className="p-5 text-sm text-slate-500">Loading…</div>
          ) : !activity.data?.activity.length ? (
            <div className="p-8 text-center text-sm text-slate-500">No activity yet.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {activity.data.activity.map((entry) => (
                <li key={entry.id} className="px-5 py-2.5 text-sm">
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <Badge tone={statusTone(entry.outcome)}>{entry.outcome}</Badge>
                      <span className="font-mono text-xs text-slate-600">{entry.action}</span>
                      {entry.target_kind && entry.target_id && (
                        <Link
                          to={
                            entry.target_kind === 'tenant'
                              ? `/tenants/${entry.target_id}`
                              : entry.target_kind === 'server'
                              ? `/servers/${entry.target_id}`
                              : '#'
                          }
                          className="text-xs text-slate-700 hover:text-slate-900 truncate"
                        >
                          {entry.target_kind}:{entry.target_id}
                        </Link>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 whitespace-nowrap">
                      {entry.operator_email ?? 'system'} · {relativeTime(entry.created_at)}
                    </div>
                  </div>
                  {entry.error_message && (
                    <div className="text-xs text-red-600 mt-1 ml-12">{entry.error_message}</div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
